/**
 * IndexedDB helper for the PhotoShop offline queue.
 *
 * Feb 2026 — dispatcher's phone might have patchy signal in the yard.
 * Photos captured via `getUserMedia` land here first (as Blob), and a
 * simple retry loop uploads them when the network comes back. Once
 * uploaded successfully the row is deleted.
 *
 * Schema (single object store `queue`):
 *   { id, rego, blob, content_type, captured_at, retry_count, last_error }
 *
 * NOT a full Background Sync API implementation (would need a
 * service-worker `sync` event handler with more moving parts). This
 * simpler retry-while-tab-open approach is fine for MVP — dispatcher
 * captures photos, walks back to a spot with signal, and by the time
 * they reach the office the tab has drained the queue.
 */

const DB_NAME = "photoshop-queue";
const DB_VERSION = 1;
const STORE = "queue";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "id" });
        os.createIndex("captured_at", "captured_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueCapture({ id, rego, blob, contentType }) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put({
      id,
      rego,
      blob,
      content_type: contentType,
      captured_at: new Date().toISOString(),
      retry_count: 0,
      last_error: null,
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listPending() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function removeById(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function markRetry(id, error) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) return resolve();
      row.retry_count = (row.retry_count || 0) + 1;
      row.last_error = error || null;
      store.put(row);
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
